import React, { useEffect, useRef, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Upload, Loader2, Play } from "lucide-react";

export default function AdminWork() {
  const [cases, setCases] = useState([]);
  const refs = useRef({});
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/cases").then((r) => setCases(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => { await api.post("/admin/cases", { title: "New case", order: cases.length }); load(); };
  const save = async (c, fields) => { await api.put(`/admin/cases/${c.id}`, fields); load(); };
  const del = async (c) => { if (window.confirm(`Delete ${c.title}?`)) { await api.delete(`/admin/cases/${c.id}`); load(); } };

  const uploadImg = async (c, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(c.id);
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "cases");
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/admin/cases/${c.id}`, { image_url: data.url });
      toast.success("Image uploaded"); load();
    } catch (err) { toast.error("Upload failed"); } finally { setBusy(null); e.target.value = ""; }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold">Work / Cases</h1><p className="mt-1 text-sm text-zinc-500">Animated case GIFs on the Work page. Add a YouTube link to open the video in a popup.</p></div>
        <button onClick={add} data-testid="add-case-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-violetHover"><Plus size={16} /> Add case</button>
      </div>
      <div className="mt-5 space-y-3">
        {cases.map((c) => (
          <div key={c.id} data-testid={`admin-case-${c.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-zinc-200 bg-white p-3">
            <div className="relative h-16 w-28 shrink-0 overflow-hidden rounded-lg bg-zinc-100">
              {c.image_url && <img src={mediaUrl(c.image_url)} alt="" className="h-full w-full object-cover" />}
              {c.youtube_url && <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-brand-violet text-white"><Play size={10} className="fill-white" /></span>}
            </div>
            <Input defaultValue={c.title} onBlur={(e) => save(c, { title: e.target.value })} placeholder="Title" className="h-9 w-40 rounded-lg" />
            <Input defaultValue={c.youtube_url} onBlur={(e) => save(c, { youtube_url: e.target.value })} placeholder="YouTube link" data-testid={`case-yt-${c.id}`} className="h-9 flex-1 min-w-[180px] rounded-lg" />
            <label className="flex items-center gap-1.5 text-xs text-zinc-600"><Switch checked={c.published} onCheckedChange={(v) => save(c, { published: v })} /> Live</label>
            <input ref={(el) => (refs.current[c.id] = el)} type="file" accept="image/*,image/gif" hidden onChange={(e) => uploadImg(c, e)} data-testid={`case-img-input-${c.id}`} />
            <button onClick={() => refs.current[c.id]?.click()} disabled={busy === c.id} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 px-3 py-2 text-xs font-semibold hover:border-brand-violet">
              {busy === c.id ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Image/GIF
            </button>
            <button onClick={() => del(c)} className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
